import isNil from 'lodash/isNil';
import { WorkflowActionType } from '../data/models/workflow.model';
import { TFunction } from 'i18next';
import {
  HandleId,
  StepNodeWidth,
} from '../components/home/workflows/WorkflowStepNode';
import {
  edgeName,
  nodeGap,
  nodeName,
  addButtonNodeName,
  WorkflowChartItem,
  branchingEdgeName,
  smallNodeName,
} from '../components/home/workflows/WorkflowFlowChart';
import { EdgeType } from '../components/home/workflows/WorkflowStepEdge';
import { SmallStepNodeWidth } from '../components/home/workflows/WorkflowSmallStepNode';
import {
  SendMessageActionFormData,
  SimplifiedWorkflowStepFormData,
} from '../data/models/simplified-workflow.model';

class WorkflowChartUtils {
  public getNodes(
    chartItems: WorkflowChartItem[],
    selectedId: string,
    nodeHeights: { [key: string]: number },
  ) {
    let nodes: any[] = [];
    const groupedChartItems = this.groupByRow(chartItems, (item) => item.row);
    let y = 0;
    groupedChartItems.forEach((chartItems, index) => {
      let rowHeight = 0;
      chartItems.forEach((chartItem) => {
        let x = 0;
        const nodeHeight = nodeHeights[chartItem.id];
        if (nodeHeight && nodeHeight > rowHeight) {
          rowHeight = nodeHeight;
        }

        let colCount = chartItem?.colInfo?.totalCols ?? 1;
        if (colCount > 1 || chartItem.isAddButton) {
          const nodeMargins = 44;
          const nodeWidth = chartItem.isAddButton
            ? 34
            : SmallStepNodeWidth + nodeMargins;
          const stepNodeWidth = StepNodeWidth + nodeMargins;
          const colCount = chartItem.colInfo?.totalCols ?? 1;
          const sliceWidth = stepNodeWidth / colCount;
          const col = chartItem.colInfo?.col ?? 0;
          x = sliceWidth * col + sliceWidth / 2 - nodeWidth / 2;
        }

        let nodeType = colCount > 1 ? smallNodeName : nodeName;
        if (chartItem.isAddButton) {
          nodeType = addButtonNodeName;
        }

        nodes.push({
          id: chartItem.id ?? '',
          position: { x, y },
          type: nodeType,
          selected: chartItem.id === selectedId,
          data: {
            row: index,
            title: chartItem.title,
            actions: chartItem.actions,
            parentId: chartItem.parentId,
            isAddButton: chartItem.isAddButton,
            onHeightChange: (id: string, height: number) => {},
          },
        });
      });
      if (rowHeight) {
        y += rowHeight + nodeGap;
      } else {
        y += 240;
      }
    });
    return nodes;
  }

  public getEdges(
    chartItems: WorkflowChartItem[],
    translateFunction: TFunction,
  ) {
    let edges: any[] = [];
    chartItems.forEach((chartItem, index) => {
      if (chartItem.onSuccess) {
        edges.push(
          this.getEdgeForAction(
            chartItem,
            index,
            translateFunction,
            WorkflowActionType.Succeed,
          ),
        );
      }
      if (chartItem.onFailure) {
        edges.push(
          this.getEdgeForAction(
            chartItem,
            index,
            translateFunction,
            WorkflowActionType.Fail,
          ),
        );
      }
      if (chartItem.onCancel) {
        edges.push(
          this.getEdgeForAction(
            chartItem,
            index,
            translateFunction,
            WorkflowActionType.Cancel,
          ),
        );
      }
      if (chartItem.onNext && chartItem.onNext.length > 0) {
        edges.push(...this.getEdgesForOnNextAction(chartItem));
      }
    });
    return edges;
  }

  private getEdgeForAction(
    chartItem: WorkflowChartItem,
    index: number,
    translateFunction: TFunction,
    action?: WorkflowActionType,
  ) {
    const source = chartItem.id;
    let target = '';
    let sourceHandle = '';
    let targetHandle = HandleId.TopCenter;
    const getTargetHandle = (nextNodeIndex?: number) => {
      if (isNil(nextNodeIndex)) {
        return HandleId.TopCenter;
      }
      return nextNodeIndex < index ? HandleId.RightCenter : HandleId.TopCenter;
    };
    switch (action) {
      case WorkflowActionType.Succeed:
        target = chartItem.onSuccess?.id ?? '';
        targetHandle = getTargetHandle(chartItem.onSuccess?.level);
        sourceHandle = HandleId.BottomCenter;
        break;
      case WorkflowActionType.Fail:
        target = chartItem.onFailure?.id ?? '';
        targetHandle = getTargetHandle(chartItem.onFailure?.level);
        sourceHandle = HandleId.BottomRight;
        break;
      case WorkflowActionType.Cancel:
        target = chartItem.onCancel?.id ?? '';
        targetHandle = getTargetHandle(chartItem.onCancel?.level);
        sourceHandle = HandleId.BottomLeft;
        break;
      default:
        if (chartItem.onNext && chartItem.onNext.length > 0) {
          target = chartItem.onNext[0]?.id ?? '';
          targetHandle = HandleId.TopCenter;
          sourceHandle = HandleId.BottomCenter;
        }
    }
    const id = `e${source}-${target}-${action}`;
    const label = action ? translateFunction(`workflows.${action}`) : undefined;
    return {
      id,
      source,
      target,
      label,
      sourceHandle,
      targetHandle,
      type: edgeName,
      selectable: false,
      data: {
        status: action,
        edgeType:
          targetHandle === HandleId.RightCenter
            ? EdgeType.Step
            : EdgeType.Bezier,
      },
    };
  }

  private getEdgesForOnNextAction(chartItem: WorkflowChartItem) {
    return (
      chartItem.onNext?.map((transition) => {
        const source = chartItem.id;
        const target = transition.id;
        const id = `e${source}-${target}`;
        return {
          id,
          source,
          target,
          label: '',
          sourceHandle: HandleId.BottomCenter,
          targetHandle: HandleId.TopCenter,
          type: branchingEdgeName,
          selectable: false,
          data: {
            label: transition?.name ?? '',
          },
        };
      }) ?? []
    );
  }

  public groupByRow = <T>(arr: T[], getRow: (item: T) => number): T[][] => {
    const grouped = arr.reduce(
      (acc, obj) => {
        const row = getRow(obj);
        if (!acc[row]) acc[row] = [];
        acc[row].push(obj);
        return acc;
      },
      {} as Record<number, T[]>,
    );

    return Object.values(grouped);
  };

  public getNodeLevel = (
    node: SimplifiedWorkflowStepFormData,
    allNodes: SimplifiedWorkflowStepFormData[],
  ): number => {
    if (!node.parentId) {
      return 0; // Root level
    }

    const parent = allNodes.find((n) => n.tempId === node.parentId);
    if (!parent) {
      return 0; // Parent not found, treat as root
    }

    return 1 + this.getNodeLevel(parent, allNodes);
  };

  public getFirstAncestorWithBranches = (
    node: SendMessageActionFormData,
    allNodes: SendMessageActionFormData[],
  ): SendMessageActionFormData | null => {
    let currentNode = node;

    while (currentNode.parentId) {
      const parent = allNodes.find((n) => n.tempId === currentNode.parentId);
      if (!parent) return null;

      if (parent.branches && parent.branches.length > 0) {
        return parent;
      }

      currentNode = parent;
    }

    return null; // No ancestor with branches found
  };

  getDescendants = (
    node: SendMessageActionFormData,
    allNodes: SendMessageActionFormData[],
  ): SendMessageActionFormData[] => {
    const descendants: SendMessageActionFormData[] = [];
    let currentLevelIds = [node.tempId];

    while (currentLevelIds.length > 0) {
      const children = allNodes.filter(
        (n) => n.parentId && currentLevelIds.includes(n.parentId),
      );

      descendants.push(...children);
      currentLevelIds = children.map((c) => c.tempId);
    }

    return descendants;
  };

  public deleteAllDescendants = (
    node: SendMessageActionFormData,
    allNodes: SendMessageActionFormData[],
  ): SendMessageActionFormData[] => {
    const descendants = this.getDescendants(node, allNodes);
    const descendantIds = new Set(descendants.map((d) => d.tempId));

    return allNodes.filter((n) => !descendantIds.has(n.tempId));
  };
}

const workflowChartUtils = new WorkflowChartUtils();

export default workflowChartUtils;
